/**
 * Tarjeta individual de tarea de mantenimiento
 */

import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  MoreVertical,
  Edit,
  Trash2,
  CheckCircle,
  Play,
  Clock,
  Sparkles,
  Scissors,
  Target,
  Droplet,
  Brush,
  Search,
  Wrench,
  Calendar,
  User,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { PlotterMaintenance } from '@/types/plotterMaintenance';
import { TASK_TYPE_CONFIG, STATUS_CONFIG } from '@/types/plotterMaintenance';
import { cn } from '@/lib/utils';

// Mapeo de iconos
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  Sparkles,
  Scissors,
  Target,
  Droplet,
  Brush,
  Search,
  Wrench,
};

interface MaintenanceCardProps {
  task: PlotterMaintenance;
  machineName?: string;
  onEdit?: (task: PlotterMaintenance) => void;
  onDelete?: (task: PlotterMaintenance) => void;
  onComplete?: (task: PlotterMaintenance) => void;
  onStart?: (task: PlotterMaintenance) => void;
  canManage?: boolean;
}

export function MaintenanceCard({
  task,
  machineName,
  onEdit,
  onDelete,
  onComplete,
  onStart,
  canManage = false,
}: MaintenanceCardProps) {
  const taskConfig = TASK_TYPE_CONFIG[task.task_type];
  const statusConfig = STATUS_CONFIG[task.status];
  const IconComponent = ICON_MAP[taskConfig.icon] || Wrench;

  const isOverdue = task.status === 'scheduled' && new Date(task.scheduled_date) < new Date();
  const canStart = task.status === 'scheduled';
  const canComplete = task.status === 'scheduled' || task.status === 'in_progress';

  return (
    <Card className={cn(
      'p-4 transition-all hover:shadow-md',
      isOverdue && 'border-red-500/50 bg-red-500/5'
    )}>
      <div className="flex items-start justify-between gap-4">
        {/* Icono y contenido principal */}
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={cn(
            'p-2 rounded-lg shrink-0',
            taskConfig.bgColor
          )}>
            <IconComponent className={cn('w-5 h-5', taskConfig.color)} />
          </div>

          <div className="flex-1 min-w-0">
            {/* Título y estado */}
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold truncate">{task.title}</h3>
              <Badge
                variant="outline"
                className={cn(
                  statusConfig.bgColor,
                  statusConfig.color,
                  statusConfig.borderColor
                )}
              >
                {statusConfig.label}
              </Badge>
              {task.is_recurring && (
                <Badge variant="secondary" className="text-xs">
                  Recurrente
                </Badge>
              )}
            </div>

            {/* Tipo de tarea */}
            <p className={cn('text-sm', taskConfig.color)}>
              {taskConfig.label}
            </p>

            {/* Detalles */}
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              {/* Fecha */}
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>
                  {format(new Date(task.scheduled_date), "dd MMM yyyy 'a las' HH:mm", { locale: es })}
                </span>
              </div>

              {/* Duración estimada */}
              <div className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                <span>{task.estimated_duration_minutes} min</span>
              </div>

              {/* Máquina */}
              {machineName && (
                <div className="flex items-center gap-1">
                  <Wrench className="w-3.5 h-3.5" />
                  <span>{machineName}</span>
                </div>
              )}
            </div>

            {/* Descripción */}
            {task.description && (
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                {task.description}
              </p>
            )}

            {/* Checklist progress */}
            {task.checklist && task.checklist.length > 0 && (
              <div className="mt-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>
                    Checklist: {task.checklist.filter(c => c.completed).length}/{task.checklist.length}
                  </span>
                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden max-w-24">
                    <div
                      className="h-full bg-green-500 transition-all"
                      style={{
                        width: `${(task.checklist.filter(c => c.completed).length / task.checklist.length) * 100}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Acciones */}
        {canManage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="shrink-0">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canStart && (
                <DropdownMenuItem onClick={() => onStart?.(task)}>
                  <Play className="w-4 h-4 mr-2" />
                  Iniciar
                </DropdownMenuItem>
              )}
              {canComplete && (
                <DropdownMenuItem onClick={() => onComplete?.(task)}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Completar
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onEdit?.(task)}>
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete?.(task)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </Card>
  );
}
